"use client";
import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination} from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { trackArrowClick } from './BannerizationAnalytics';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { componentNameConstants } from '@repo/analytics/data-layer.constants';

interface BannerCarouselProps {
  children: React.ReactNode[];
  isLoop: string;
  autoplay: string;
  autoTimer: number;
  moduleName: string;
  sectionName: string;
}

const BannerCarousel: React.FC<BannerCarouselProps> = ({ 
  children, 
  isLoop, 
  moduleName,
  sectionName,
  autoplay,
  autoTimer
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const isAutoplayEnabled = autoplay === 'true';
  const isLoopEnabled = isLoop === 'true';

  const handlePrevClick = () => {
    trackArrowClick(componentNameConstants.BANNER_CAROUSEL, 'left', moduleName,sectionName);
    swiperRef.current?.slidePrev();
  };

  const handleNextClick = () => {
    trackArrowClick(componentNameConstants.BANNER_CAROUSEL, 'right', moduleName,sectionName);
    swiperRef.current?.slideNext();
  };

  const updateNavigationState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <Swiper
      modules={[Autoplay, Navigation, Pagination]}
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
        updateNavigationState(swiper);
      }}
      onSlideChange={(swiper) => {
        updateNavigationState(swiper);
      }}
      slidesPerView={1}
      autoplay={isAutoplayEnabled ? {
        delay: autoTimer*1000 || 4000,
        disableOnInteraction: false,
      } : false}  
      navigation={false}
      loop={isLoopEnabled || false}
      pagination={{
        clickable: true,
        el: '.banner-swiper-pagination',
      }}
      className="banner-swiper"
    >
      {children.map((child, index) => (
        <SwiperSlide key={`${moduleName}-${sectionName}-${index}`}>
          {child}
        </SwiperSlide>
      ))}
      
      {(!isLoopEnabled && !isBeginning || isLoopEnabled) && (
        <button 
          onClick={handlePrevClick}
          className="banner-swiper-button-prev text-link-primary absolute left-0 top-[46%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-r-md bg-white p-3 shadow-[0_0_10px_rgba(0,0,0,0.2)] md:flex"
          aria-label="Previous slide"
        >
          <i className="icomoon text-sm before:content-['\e912']"></i>
        </button>
      )}
      
      {(!isLoopEnabled && !isEnd || isLoopEnabled) && (
        <button 
          onClick={handleNextClick}
          className="banner-swiper-button-next text-link-primary absolute right-0 top-[46%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-l-md bg-white p-3 shadow-[0_0_10px_rgba(0,0,0,0.2)] md:flex"
          aria-label="Next slide"
        >
          <i className="icomoon text-sm before:content-['\e911']"></i>
        </button>
      )}

      <div className="banner-swiper-pagination absolute bottom-4 left-0 right-0 z-10 flex justify-center"></div>
    </Swiper>
  );
};

export default BannerCarousel;